import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getBrokerDashboardSummary, 
  getBrokerPipeline, 
  getProspectDetails,
  getProspectDocuments,
  updateProspectStatus,
  activateCustomer,
  createIntakeLink,
  getIntakeLinks,
  updateIntakeLink,
  revokeIntakeLink,
  resendIntakeInvite,
  getBrokers,
  createBroker,
  updateBroker,
  deleteBroker,
  downloadProspectDocument,
  brokerUploadDocument
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Users, 
  Link as LinkIcon, 
  Building2, 
  TrendingUp, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Plus,
  Copy,
  ExternalLink,
  FileText,
  Download,
  Shield,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  UserPlus,
  Briefcase,
  Target,
  BarChart3,
  Eye,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  History,
  Sparkles,
  CheckCheck,
  Send,
  FileUp,
  FileDown,
  ArrowRight,
  Building,
  Globe,
  Edit,
  MoreVertical,
  Lock
} from 'lucide-react';
import { cn } from '../lib/utils';

// Pipeline stage colors and display names
const STAGE_CONFIG = {
  lead: { color: 'bg-slate-500', label: 'Lead' },
  prospect: { color: 'bg-blue-500', label: 'Prospect' },
  intake_in_progress: { color: 'bg-yellow-500', label: 'Intake In Progress' },
  assessment_completed: { color: 'bg-orange-500', label: 'Assessment Done' },
  documents_uploaded: { color: 'bg-purple-500', label: 'Docs Uploaded' },
  under_review: { color: 'bg-indigo-500', label: 'Under Review' },
  proposal_sent: { color: 'bg-cyan-500', label: 'Proposal Sent' },
  closed_won: { color: 'bg-green-500', label: 'Closed Won' },
  closed_lost: { color: 'bg-red-500', label: 'Closed Lost' },
  customer_active: { color: 'bg-emerald-500', label: 'Active Customer' },
  inactive: { color: 'bg-gray-500', label: 'Inactive' }
};

const getRiskBadge = (level) => {
  const config = {
    low: { color: 'bg-green-100 text-green-700', label: 'Low Risk' },
    medium: { color: 'bg-yellow-100 text-yellow-700', label: 'Medium Risk' },
    high: { color: 'bg-red-100 text-red-700', label: 'High Risk' }
  };
  return config[level] || config.medium;
};

// Helper function to safely extract error message from API responses
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors are arrays of objects with 'msg' field
    return detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) {
    return detail.msg;
  }
  return fallback;
};

export default function BrokerDashboard() {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin(); // Call the function to get the boolean
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [intakeLinks, setIntakeLinks] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [selectedTab, setSelectedTab] = useState('pipeline');
  const [stageFilter, setStageFilter] = useState('all');
  
  // Dialog states
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [showEditLink, setShowEditLink] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [showCreateBroker, setShowCreateBroker] = useState(false);
  const [showEditBroker, setShowEditBroker] = useState(false);
  const [showDeleteBroker, setShowDeleteBroker] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [showProspectDetail, setShowProspectDetail] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [prospectDocs, setProspectDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('certificate_of_insurance');
  const [uploadDescription, setUploadDescription] = useState('');
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({ status: '', notes: '' });
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [activationNotes, setActivationNotes] = useState('');
  const [activating, setActivating] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  
  // Broker edit form data
  const [editBrokerData, setEditBrokerData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    license_number: '',
    is_active: true
  });
  
  // Form data
  const [linkFormData, setLinkFormData] = useState({
    organization_name: '',
    contact_name: '',
    contact_email: '',
    org_type: 'sports',
    sport_type: '',
    renewal_date: '',
    notes: '',
    expires_in_days: 30
  });

  // Edit link form data
  const [editLinkFormData, setEditLinkFormData] = useState({
    organization_name: '',
    contact_name: '',
    contact_email: '',
    org_type: 'sports',
    sport_type: '',
    renewal_date: '',
    notes: '',
    expires_in_days: 30
  });
  
  const [brokerFormData, setBrokerFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    license_number: ''
  });

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, pipelineRes, linksRes] = await Promise.all([
        getBrokerDashboardSummary(),
        getBrokerPipeline(stageFilter !== 'all' ? stageFilter : null),
        getIntakeLinks()
      ]);
      
      setSummary(summaryRes.data);
      setPipeline(pipelineRes.data);
      setIntakeLinks(linksRes.data);
      
      // Load brokers only for super admin
      if (isAdmin) {
        const brokersRes = await getBrokers();
        setBrokers(brokersRes.data);
      }
    } catch (error) {
      console.error('Failed to load broker dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create intake link
  const handleCreateLink = async () => {
    // Validation: organization name is required
    if (!linkFormData.organization_name || !linkFormData.organization_name.trim()) {
      toast.error('Organization Name is required');
      return;
    }
    try {
      const res = await createIntakeLink(linkFormData);
      toast.success('Intake link created successfully!');
      setIntakeLinks(prev => [res.data, ...prev]);
      setShowCreateLink(false);
      setLinkFormData({
        organization_name: '',
        contact_name: '',
        contact_email: '',
        org_type: 'sports',
        sport_type: '',
        renewal_date: '',
        notes: '',
        expires_in_days: 30
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create link'));
    }
  };

  // Open edit link dialog
  const handleOpenEditLink = (link) => {
    setSelectedLink(link);
    setEditLinkFormData({
      organization_name: link.organization_name || '',
      contact_name: link.contact_name || '',
      contact_email: link.contact_email || '',
      org_type: link.org_type || 'sports',
      sport_type: link.sport_type || '',
      renewal_date: link.renewal_date || '',
      notes: link.notes || '',
      expires_in_days: 30 // Default extension period
    });
    setShowEditLink(true);
  };

  // Update intake link
  const handleUpdateLink = async () => {
    if (!selectedLink) return;
    try {
      const res = await updateIntakeLink(selectedLink.id, editLinkFormData);
      toast.success('Intake link updated successfully!');
      setIntakeLinks(prev => prev.map(l => l.id === selectedLink.id ? res.data : l));
      setShowEditLink(false);
      setSelectedLink(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update link'));
    }
  };

  // Create broker
  const handleCreateBroker = async () => {
    try {
      await createBroker(brokerFormData);
      toast.success('Broker account created successfully!');
      setShowCreateBroker(false);
      setBrokerFormData({
        email: '',
        password: '',
        name: '',
        company_name: '',
        phone: '',
        license_number: ''
      });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create broker'));
    }
  };

  // Open edit broker dialog
  const handleOpenEditBroker = (broker) => {
    setSelectedBroker(broker);
    setEditBrokerData({
      email: broker.email || '',
      password: '', // Don't pre-fill password
      name: broker.name || '',
      company_name: broker.company_name || '',
      phone: broker.phone || '',
      license_number: broker.license_number || '',
      is_active: broker.is_active !== false
    });
    setShowEditBroker(true);
  };

  // Handle update broker
  const handleUpdateBroker = async () => {
    if (!selectedBroker) return;
    try {
      // Only send fields that have changed
      const updatePayload = {};
      if (editBrokerData.email && editBrokerData.email !== selectedBroker.email) {
        updatePayload.email = editBrokerData.email;
      }
      if (editBrokerData.password) {
        updatePayload.password = editBrokerData.password;
      }
      if (editBrokerData.name !== selectedBroker.name) {
        updatePayload.name = editBrokerData.name;
      }
      if (editBrokerData.company_name !== selectedBroker.company_name) {
        updatePayload.company_name = editBrokerData.company_name;
      }
      if (editBrokerData.phone !== selectedBroker.phone) {
        updatePayload.phone = editBrokerData.phone;
      }
      if (editBrokerData.license_number !== selectedBroker.license_number) {
        updatePayload.license_number = editBrokerData.license_number;
      }
      if (editBrokerData.is_active !== selectedBroker.is_active) {
        updatePayload.is_active = editBrokerData.is_active;
      }

      await updateBroker(selectedBroker.id, updatePayload);
      toast.success('Broker updated successfully!');
      setShowEditBroker(false);
      setSelectedBroker(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update broker'));
    }
  };

  // Handle delete broker
  const handleDeleteBroker = async () => {
    if (!selectedBroker) return;
    try {
      await deleteBroker(selectedBroker.id);
      toast.success('Broker deleted successfully!');
      setShowDeleteBroker(false);
      setSelectedBroker(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete broker'));
    }
  };

  // Copy link to clipboard with fallback
  const copyLink = async (url) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
      } else {
        // Fallback for older browsers or restricted environments
        fallbackCopyToClipboard(url);
      }
    } catch (err) {
      // Fallback if clipboard API is blocked
      fallbackCopyToClipboard(url);
    }
  };

  // Fallback copy method using textarea
  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success('Link copied to clipboard!');
    } catch (err) {
      // If all copy methods fail, show the link in a toast so user can manually copy
      toast.info(
        <div>
          <p className="font-medium mb-1">Copy this link:</p>
          <code className="text-xs break-all">{text}</code>
        </div>,
        { duration: 10000 }
      );
    }
    document.body.removeChild(textArea);
  };

  // View prospect details
  const viewProspect = async (prospect) => {
    // Load details and documents independently so a missing intake record
    // (e.g. converted client orgs) never blocks opening the docs view.
    const [detailRes, docsRes] = await Promise.allSettled([
      getProspectDetails(prospect.organization_id),
      getProspectDocuments(prospect.organization_id)
    ]);

    if (detailRes.status === 'fulfilled') {
      setSelectedProspect({ ...prospect, ...detailRes.value.data });
    } else {
      setSelectedProspect({ ...prospect });
    }
    setProspectDocs(docsRes.status === 'fulfilled' ? docsRes.value.data : []);

    if (detailRes.status === 'rejected' && docsRes.status === 'rejected') {
      toast.error('Failed to load organization details');
      return;
    }
    setShowProspectDetail(true);
  };

  // Update status
  const handleStatusUpdate = async () => {
    if (!selectedProspect || !statusUpdateData.status) return;
    try {
      await updateProspectStatus(
        selectedProspect.organization_id,
        statusUpdateData.status,
        statusUpdateData.notes
      );
      toast.success('Status updated successfully!');
      setShowStatusUpdate(false);
      setStatusUpdateData({ status: '', notes: '' });
      loadData();
      // Update selected prospect
      setSelectedProspect(prev => ({
        ...prev,
        pipeline_stage: statusUpdateData.status
      }));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update status'));
    }
  };

  // Activate customer
  const handleActivateCustomer = async () => {
    if (!selectedProspect) return;
    setActivating(true);
    try {
      const res = await activateCustomer(selectedProspect.organization_id, activationNotes || 'Activated via broker dashboard');
      toast.success(
        <div>
          <p className="font-semibold">{res.data.organization_name} is now an active customer!</p>
          <p className="text-sm mt-1">Email notification sent to organization contact.</p>
          <p className="text-sm">Modules unlocked: {res.data.modules_unlocked?.length || 8}</p>
        </div>
      );
      setShowActivateConfirm(false);
      setShowProspectDetail(false);
      setActivationNotes('');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to activate customer'));
    } finally {
      setActivating(false);
    }
  };

  // Download document - using temporary token for maximum browser compatibility
  const handleDownloadDoc = async (doc) => {
    setDownloadingDoc(doc.id);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Step 1: Get a temporary download token from the server
      const tokenResponse = await fetch(
        `${baseUrl}/api/broker/prospects/${selectedProspect.organization_id}/documents/${doc.id}/download-token`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!tokenResponse.ok) {
        throw new Error(`Failed to get download token: ${tokenResponse.status}`);
      }
      
      const { download_token } = await tokenResponse.json();
      const downloadUrl = `${baseUrl}/api/broker/download/${download_token}`;
      
      // Step 2: Use an iframe for download (doesn't navigate away from page)
      // This is the most reliable cross-browser download method
      let downloadFrame = document.getElementById('download-frame');
      if (!downloadFrame) {
        downloadFrame = document.createElement('iframe');
        downloadFrame.id = 'download-frame';
        downloadFrame.style.display = 'none';
        document.body.appendChild(downloadFrame);
      }
      
      // Set iframe src to trigger download
      downloadFrame.src = downloadUrl;
      
      toast.success(`Downloading ${doc.original_filename}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download: ${error.message}`);
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Revoke link
  const handleRevokeLink = async (linkId) => {
    try {
      await revokeIntakeLink(linkId);
      toast.success('Link revoked');
      setIntakeLinks(prev => prev.map(l => l.id === linkId ? { ...l, status: 'revoked' } : l));
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  // Resend intake invite email
  const handleResendInvite = async (linkId) => {
    try {
      const res = await resendIntakeInvite(linkId);
      toast.success(res.data?.message || 'Invite email sent');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send invite email'));
    }
  };

  // Broker upload document for a prospect
  const handleBrokerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProspect) return;
    setUploadingDoc(true);
    try {
      // Use organization_id from the prospect object
      const orgId = selectedProspect.organization_id || selectedProspect.id;
      await brokerUploadDocument(orgId, file, uploadCategory, uploadDescription);
      toast.success(`Uploaded ${file.name} successfully`);
      setShowUploadDialog(false);
      setUploadDescription('');
      // Refresh docs
      const docsRes = await getProspectDocuments(orgId);
      setProspectDocs(docsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="broker-dashboard-title">
            Broker Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage prospects, intake links, and customer onboarding
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowCreateLink(true)} data-testid="create-intake-link-btn" size="sm" className="text-xs sm:text-sm">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Create Intake Link</span>
            <span className="sm:hidden">New Link</span>
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowCreateBroker(true)} data-testid="create-broker-btn" size="sm" className="text-xs sm:text-sm">
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Broker</span>
              <span className="sm:hidden">Add</span>
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.total_prospects || 0}</p>
                <p className="text-xs text-muted-foreground">Total Prospects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.active_prospects || 0}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Target className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.awaiting_action || 0}</p>
                <p className="text-xs text-muted-foreground">Awaiting Action</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.closed_won || 0}</p>
                <p className="text-xs text-muted-foreground">Closed Won</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.closed_lost || 0}</p>
                <p className="text-xs text-muted-foreground">Closed Lost</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.conversion_rate || 0}%</p>
                <p className="text-xs text-muted-foreground">Conversion</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="pipeline" data-testid="pipeline-tab">
            <Building2 className="w-4 h-4 mr-2" />
            Pipeline ({pipeline.length})
          </TabsTrigger>
          <TabsTrigger value="links" data-testid="links-tab">
            <LinkIcon className="w-4 h-4 mr-2" />
            Intake Links ({intakeLinks.length})
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="brokers" data-testid="brokers-tab">
              <Briefcase className="w-4 h-4 mr-2" />
              Brokers ({brokers.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {Object.entries(STAGE_CONFIG).map(([value, config]) => (
                  <SelectItem key={value} value={value}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {pipeline.length === 0 ? (
            <Card className="p-8 text-center">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No prospects yet</h3>
              <p className="text-muted-foreground mb-4">
                Create an intake link to start onboarding organizations
              </p>
              <Button onClick={() => setShowCreateLink(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Intake Link
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {pipeline.map((prospect) => (
                <Card 
                  key={prospect.organization_id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => viewProspect(prospect)}
                  data-testid={`prospect-card-${prospect.organization_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold">{prospect.organization_name}</h3>
                          <Badge 
                            className={cn(
                              "text-white text-xs",
                              STAGE_CONFIG[prospect.pipeline_stage]?.color || 'bg-gray-500'
                            )}
                          >
                            {STAGE_CONFIG[prospect.pipeline_stage]?.label || prospect.pipeline_stage}
                          </Badge>
                          {prospect.risk_score !== null && (
                            <Badge className={getRiskBadge(prospect.risk_level).color}>
                              Score: {prospect.risk_score?.toFixed(0)}%
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {prospect.primary_contact && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {prospect.primary_contact}
                            </span>
                          )}
                          {prospect.contact_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {prospect.contact_email}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {prospect.documents_count} docs
                          </span>
                        </div>
                      </div>
                      
                      {/* Progress indicators */}
                      <div className="flex items-center gap-4">
                        <div className="flex gap-2">
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                            prospect.intake_started ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                          )}>
                            1
                          </div>
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                            prospect.assessment_completed ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                          )}>
                            2
                          </div>
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                            prospect.policies_uploaded ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                          )}>
                            3
                          </div>
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                            prospect.loss_runs_uploaded ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                          )}>
                            4
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Intake Links Tab */}
        <TabsContent value="links" className="space-y-4">
          {/* Broker-Assisted Intake Info Card */}
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-1">Broker-Assisted Intake</h4>
                <p className="text-sm text-muted-foreground">
                  You can complete the intake on behalf of any prospect. Use the link below to start their intake yourself,
                  or upload their policy & loss-run documents directly from the prospect's detail view. The system tracks every action against your broker account.
                </p>
              </div>
            </CardContent>
          </Card>
          {intakeLinks.length === 0 ? (
            <Card className="p-8 text-center">
              <LinkIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No intake links created</h3>
              <p className="text-muted-foreground mb-4">
                Create links to send to organizations for intake
              </p>
              <Button onClick={() => setShowCreateLink(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Intake Link
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {intakeLinks.map((link) => (
                <Card key={link.id} data-testid={`intake-link-${link.id}`} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Organization Name & Tags */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h4 className="font-semibold text-base">{link.organization_name || 'Unnamed Organization'}</h4>
                          <Badge 
                            variant={link.status === 'active' ? 'default' : link.status === 'used' ? 'secondary' : 'destructive'}
                            className="text-xs"
                          >
                            {link.status}
                          </Badge>
                          <Badge variant="outline" className="text-xs capitalize">{link.org_type}</Badge>
                          {link.sport_type && (
                            <Badge variant="outline" className="text-xs">{link.sport_type}</Badge>
                          )}
                        </div>
                        
                        {/* Contact Info & Expiration */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          {link.contact_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {link.contact_name}
                            </span>
                          )}
                          {link.contact_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {link.contact_email}
                            </span>
                          )}
                          {link.expires_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Expires: {new Date(link.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        {/* Link URL */}
                        <code className="text-xs bg-muted px-2 py-1 rounded block max-w-lg truncate text-muted-foreground">
                          {link.link_url}
                        </code>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 ml-4">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => copyLink(link.link_url)}
                          title="Copy link"
                          data-testid={`copy-link-${link.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {link.status === 'active' && !link.intake_started && (
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleOpenEditLink(link)}
                            title="Edit link"
                            data-testid={`edit-link-${link.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => window.open(link.link_url, '_blank')}
                          title="Open link"
                          data-testid={`open-link-${link.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        {link.status === 'active' && link.contact_email && (
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleResendInvite(link.id)}
                            title="Resend invite email"
                            data-testid={`resend-invite-${link.id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        {link.status === 'active' && !link.intake_started && (
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleRevokeLink(link.id)}
                            title="Revoke link"
                            data-testid={`revoke-link-${link.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Brokers Tab (Super Admin Only) */}
        {isAdmin && (
          <TabsContent value="brokers" className="space-y-4">
            {brokers.length === 0 ? (
              <Card className="p-8 text-center">
                <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No brokers registered</h3>
                <p className="text-muted-foreground mb-4">
                  Add broker accounts to allow them to manage prospects
                </p>
                <Button onClick={() => setShowCreateBroker(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Broker
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {brokers.map((broker) => (
                  <Card key={broker.id} data-testid={`broker-card-${broker.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold truncate">{broker.name}</h4>
                          <p className="text-sm text-muted-foreground truncate">{broker.email}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Badge variant={broker.is_active ? 'default' : 'secondary'}>
                            {broker.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      {broker.company_name && (
                        <p className="text-sm mb-2 text-muted-foreground">{broker.company_name}</p>
                      )}
                      {broker.phone && (
                        <p className="text-sm mb-2 flex items-center gap-1 text-muted-foreground">
                          <Phone className="w-3 h-3" /> {broker.phone}
                        </p>
                      )}
                      <div className="grid grid-cols-3 gap-2 text-center mt-4">
                        <div className="bg-muted rounded p-2">
                          <p className="text-lg font-bold">{broker.organizations_count}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div className="bg-muted rounded p-2">
                          <p className="text-lg font-bold">{broker.active_prospects_count}</p>
                          <p className="text-xs text-muted-foreground">Active</p>
                        </div>
                        <div className="bg-muted rounded p-2">
                          <p className="text-lg font-bold">{broker.closed_won_count}</p>
                          <p className="text-xs text-muted-foreground">Won</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4 pt-4 border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleOpenEditBroker(broker)}
                          data-testid={`edit-broker-${broker.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => {
                            setSelectedBroker(broker);
                            setShowDeleteBroker(true);
                          }}
                          data-testid={`delete-broker-${broker.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Create Intake Link Dialog */}
      <Dialog open={showCreateLink} onOpenChange={setShowCreateLink}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Intake Link</DialogTitle>
            <DialogDescription>
              Generate a link for an organization to complete their intake process
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Organization Name <span className="text-red-500">*</span></Label>
              <Input
                value={linkFormData.organization_name}
                onChange={(e) => setLinkFormData({ ...linkFormData, organization_name: e.target.value })}
                placeholder="e.g., Metro Soccer Club"
                required
                data-testid="create-link-org-name"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Required — prevents downstream data ambiguity in your pipeline.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={linkFormData.contact_name}
                  onChange={(e) => setLinkFormData({ ...linkFormData, contact_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={linkFormData.contact_email}
                  onChange={(e) => setLinkFormData({ ...linkFormData, contact_email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Organization Type</Label>
                <Select 
                  value={linkFormData.org_type} 
                  onValueChange={(value) => setLinkFormData({ ...linkFormData, org_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sports">Youth Sports</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="professional_sports">Professional Sports</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expires In (Days)</Label>
                <Input
                  type="number"
                  value={linkFormData.expires_in_days}
                  onChange={(e) => setLinkFormData({ ...linkFormData, expires_in_days: parseInt(e.target.value) })}
                  min={1}
                  max={365}
                />
              </div>
            </div>
            <div>
              <Label>GL Policy Renewal Date</Label>
              <Input
                type="date"
                value={linkFormData.renewal_date}
                onChange={(e) => setLinkFormData({ ...linkFormData, renewal_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={linkFormData.notes}
                onChange={(e) => setLinkFormData({ ...linkFormData, notes: e.target.value })}
                placeholder="Internal notes about this prospect..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateLink(false)}>Cancel</Button>
            <Button onClick={handleCreateLink}>Create Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Intake Link Dialog */}
      <Dialog open={showEditLink} onOpenChange={setShowEditLink}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Intake Link
            </DialogTitle>
            <DialogDescription>
              Update the intake link details. Note: Links that have already been used cannot be edited.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Organization Name</Label>
              <Input
                value={editLinkFormData.organization_name}
                onChange={(e) => setEditLinkFormData({ ...editLinkFormData, organization_name: e.target.value })}
                placeholder="e.g., Metro Soccer Club"
                data-testid="edit-link-org-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={editLinkFormData.contact_name}
                  onChange={(e) => setEditLinkFormData({ ...editLinkFormData, contact_name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="edit-link-contact-name"
                />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={editLinkFormData.contact_email}
                  onChange={(e) => setEditLinkFormData({ ...editLinkFormData, contact_email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="edit-link-contact-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Organization Type</Label>
                <Select 
                  value={editLinkFormData.org_type} 
                  onValueChange={(value) => setEditLinkFormData({ ...editLinkFormData, org_type: value })}
                >
                  <SelectTrigger data-testid="edit-link-org-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sports">Youth Sports</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="professional_sports">Professional Sports</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Extend Expiration (Days)</Label>
                <Input
                  type="number"
                  value={editLinkFormData.expires_in_days}
                  onChange={(e) => setEditLinkFormData({ ...editLinkFormData, expires_in_days: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={365}
                  data-testid="edit-link-expires"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Set to 0 to keep current expiration
                </p>
              </div>
            </div>
            <div>
              <Label>GL Policy Renewal Date</Label>
              <Input
                type="date"
                value={editLinkFormData.renewal_date}
                onChange={(e) => setEditLinkFormData({ ...editLinkFormData, renewal_date: e.target.value })}
                data-testid="edit-link-renewal-date"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editLinkFormData.notes}
                onChange={(e) => setEditLinkFormData({ ...editLinkFormData, notes: e.target.value })}
                placeholder="Internal notes about this prospect..."
                rows={2}
                data-testid="edit-link-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditLink(false)}>Cancel</Button>
            <Button onClick={handleUpdateLink} data-testid="save-edit-link-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Broker Dialog */}
      <Dialog open={showCreateBroker} onOpenChange={setShowCreateBroker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Broker Account</DialogTitle>
            <DialogDescription>
              Create a new broker account to manage prospect intake
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={brokerFormData.name}
                onChange={(e) => setBrokerFormData({ ...brokerFormData, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={brokerFormData.email}
                onChange={(e) => setBrokerFormData({ ...brokerFormData, email: e.target.value })}
                placeholder="john@brokerfirm.com"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={brokerFormData.password}
                onChange={(e) => setBrokerFormData({ ...brokerFormData, password: e.target.value })}
                placeholder="Create a strong password"
              />
            </div>
            <div>
              <Label>Company Name</Label>
              <Input
                value={brokerFormData.company_name}
                onChange={(e) => setBrokerFormData({ ...brokerFormData, company_name: e.target.value })}
                placeholder="Insurance Brokerage LLC"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={brokerFormData.phone}
                  onChange={(e) => setBrokerFormData({ ...brokerFormData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>License Number</Label>
                <Input
                  value={brokerFormData.license_number}
                  onChange={(e) => setBrokerFormData({ ...brokerFormData, license_number: e.target.value })}
                  placeholder="BRK-123456"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateBroker(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateBroker}
              disabled={!brokerFormData.name || !brokerFormData.email || !brokerFormData.password}
            >
              Create Broker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Broker Dialog */}
      <Dialog open={showEditBroker} onOpenChange={setShowEditBroker}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Broker
            </DialogTitle>
            <DialogDescription>
              Update broker account information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name *</label>
              <Input
                placeholder="Full name"
                value={editBrokerData.name}
                onChange={(e) => setEditBrokerData({...editBrokerData, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                placeholder="Email address"
                value={editBrokerData.email}
                onChange={(e) => setEditBrokerData({...editBrokerData, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                <Lock className="w-4 h-4" />
                New Password (leave blank to keep current)
              </label>
              <Input
                type="password"
                placeholder="Enter new password"
                value={editBrokerData.password}
                onChange={(e) => setEditBrokerData({...editBrokerData, password: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Company Name</label>
              <Input
                placeholder="Company or brokerage name"
                value={editBrokerData.company_name}
                onChange={(e) => setEditBrokerData({...editBrokerData, company_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone</label>
              <Input
                placeholder="Phone number"
                value={editBrokerData.phone}
                onChange={(e) => setEditBrokerData({...editBrokerData, phone: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">License Number</label>
              <Input
                placeholder="Broker license number"
                value={editBrokerData.license_number}
                onChange={(e) => setEditBrokerData({...editBrokerData, license_number: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="broker-active"
                checked={editBrokerData.is_active}
                onChange={(e) => setEditBrokerData({...editBrokerData, is_active: e.target.checked})}
                className="h-4 w-4"
              />
              <label htmlFor="broker-active" className="text-sm font-medium">Active</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBroker(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateBroker} disabled={!editBrokerData.name || !editBrokerData.email}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Broker Confirmation Dialog */}
      <Dialog open={showDeleteBroker} onOpenChange={setShowDeleteBroker}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete Broker
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this broker account?
            </DialogDescription>
          </DialogHeader>
          {selectedBroker && (
            <div className="py-4">
              <div className="bg-muted rounded-lg p-4 mb-4">
                <p className="font-semibold">{selectedBroker.name}</p>
                <p className="text-sm text-muted-foreground">{selectedBroker.email}</p>
                {selectedBroker.company_name && (
                  <p className="text-sm text-muted-foreground">{selectedBroker.company_name}</p>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. The broker will lose access to their account.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteBroker(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteBroker}>
              Delete Broker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prospect Detail Sheet - Enhanced */}
      <Sheet open={showProspectDetail} onOpenChange={setShowProspectDetail}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedProspect?.organization_name}
            </SheetTitle>
            <SheetDescription>
              Complete prospect intake details and documentation
            </SheetDescription>
          </SheetHeader>
          
          {selectedProspect && (
            <ScrollArea className="h-[calc(100vh-120px)] pr-4">
              <div className="space-y-6 mt-6">
                {/* Status & Actions Bar */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge 
                      className={cn(
                        "text-white px-3 py-1",
                        STAGE_CONFIG[selectedProspect.pipeline_stage]?.color || 'bg-gray-500'
                      )}
                    >
                      {STAGE_CONFIG[selectedProspect.pipeline_stage]?.label || selectedProspect.pipeline_stage}
                    </Badge>
                    {selectedProspect.created_at && (
                      <span className="text-xs text-muted-foreground">
                        Created {new Date(selectedProspect.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowStatusUpdate(true)}
                    >
                      Update Status
                    </Button>
                    {selectedProspect.pipeline_stage === 'closed_won' && (
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setShowActivateConfirm(true)}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Activate Customer
                      </Button>
                    )}
                  </div>
                </div>

                {/* Intake Progress */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Intake Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Progress value={selectedProspect.overall_progress || 0} className="mb-3 h-2" />
                    <div className="flex justify-between text-sm mb-4">
                      <span className="text-muted-foreground">Overall Progress</span>
                      <span className="font-semibold">{selectedProspect.overall_progress || 0}% Complete</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'step_organization_profile', label: 'Organization Profile', icon: Building },
                        { key: 'step_risk_assessment', label: 'Risk Assessment', icon: Shield },
                        { key: 'step_policy_upload', label: 'Policy Upload', icon: FileUp },
                        { key: 'step_loss_run_upload', label: 'Loss Run Upload', icon: FileDown },
                      ].map((step, idx) => (
                        <div 
                          key={step.key}
                          className={cn(
                            "flex items-center gap-2 p-2 rounded-md border",
                            selectedProspect[step.key] === 'completed' 
                              ? "bg-green-50 border-green-200" 
                              : "bg-gray-50 border-gray-200"
                          )}
                        >
                          <div className={cn(
                            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                            selectedProspect[step.key] === 'completed' 
                              ? "bg-green-500 text-white" 
                              : "bg-gray-300 text-gray-600"
                          )}>
                            {selectedProspect[step.key] === 'completed' ? (
                              <CheckCheck className="w-3 h-3" />
                            ) : (
                              idx + 1
                            )}
                          </div>
                          <span className={cn(
                            "text-xs font-medium",
                            selectedProspect[step.key] === 'completed' ? "text-green-700" : "text-gray-600"
                          )}>
                            {step.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Organization Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Organization Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Organization Type</Label>
                        <p className="text-sm font-medium capitalize">{selectedProspect.org_type || 'Sports'}</p>
                      </div>
                      {selectedProspect.sport_type && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Sport/Activity</Label>
                          <p className="text-sm font-medium">{selectedProspect.sport_type}</p>
                        </div>
                      )}
                      {selectedProspect.participant_count && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Participants</Label>
                          <p className="text-sm font-medium">{selectedProspect.participant_count?.toLocaleString()}</p>
                        </div>
                      )}
                      {selectedProspect.team_count && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Teams</Label>
                          <p className="text-sm font-medium">{selectedProspect.team_count}</p>
                        </div>
                      )}
                      {selectedProspect.gl_policy_renewal_date && (
                        <div>
                          <Label className="text-xs text-muted-foreground">GL Renewal Date</Label>
                          <p className="text-sm font-medium">{new Date(selectedProspect.gl_policy_renewal_date).toLocaleDateString()}</p>
                        </div>
                      )}
                    </div>
                    
                    <Separator className="my-4" />
                    
                    {/* Contact Info */}
                    <div className="space-y-3">
                      <Label className="text-xs text-muted-foreground">Contact Information</Label>
                      {selectedProspect.primary_contact && (
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{selectedProspect.primary_contact}</span>
                        </div>
                      )}
                      {selectedProspect.contact_email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <a href={`mailto:${selectedProspect.contact_email}`} className="text-primary hover:underline">
                            {selectedProspect.contact_email}
                          </a>
                        </div>
                      )}
                      {selectedProspect.contact_phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedProspect.contact_phone}</span>
                        </div>
                      )}
                      {(selectedProspect.city || selectedProspect.state) && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span>
                            {[selectedProspect.address, selectedProspect.city, selectedProspect.state, selectedProspect.zip_code]
                              .filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Risk Assessment Results */}
                {selectedProspect.risk_score !== null && selectedProspect.risk_score !== undefined && (
                  <Card className="border-l-4 border-l-primary">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Risk Assessment Results
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-4 p-4 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-4xl font-bold">{selectedProspect.risk_score?.toFixed(0)}%</p>
                          <p className="text-sm text-muted-foreground">Risk Score</p>
                        </div>
                        <Badge className={cn("text-lg px-4 py-2", getRiskBadge(selectedProspect.risk_level).color)}>
                          {getRiskBadge(selectedProspect.risk_level).label}
                        </Badge>
                      </div>
                      {selectedProspect.recommendations?.length > 0 && (
                        <div>
                          <p className="font-medium mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                            Recommendations ({selectedProspect.recommendations.length})
                          </p>
                          <ul className="space-y-2">
                            {selectedProspect.recommendations.map((rec, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm p-2 bg-yellow-50 rounded border border-yellow-100">
                                <ArrowRight className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Documents - Enhanced with upload and download */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Uploaded Documents ({prospectDocs.length})
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {prospectDocs.length > 0 && (
                          <Badge variant="outline">{prospectDocs.length} files</Badge>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowUploadDialog(true)}
                          data-testid="broker-upload-btn"
                          className="h-7 text-xs"
                        >
                          <FileUp className="w-3.5 h-3.5 mr-1" />
                          Upload
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {prospectDocs.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No documents uploaded yet</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowUploadDialog(true)}
                          className="mt-2 text-xs"
                        >
                          <FileUp className="w-3.5 h-3.5 mr-1" />
                          Upload a document
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Group by category type - simplified categories */}
                        {[
                          { key: 'insurance', label: 'Insurance Policies', match: ['insurance_policies'] },
                          { key: 'loss_runs', label: 'Loss Runs', match: ['loss_runs', 'loss_run', 'claims_history'] },
                          { key: 'coi', label: 'Certificates of Insurance', match: ['certificate_of_insurance'] }
                        ].map(group => {
                          const groupDocs = prospectDocs.filter(d => 
                            group.match.some(m => d.category?.includes(m))
                          );
                          
                          // If no docs match specific groups, put them in a catch-all
                          const ungroupedDocs = prospectDocs.filter(d => 
                            !['insurance_policies', 'loss_runs', 'loss_run', 'claims_history', 'certificate_of_insurance']
                              .some(m => d.category?.includes(m))
                          );
                          
                          if (groupDocs.length === 0 && group.key !== 'other') return null;
                          
                          return (
                            <div key={group.key} className="mb-4">
                              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                                {group.label} ({groupDocs.length})
                              </p>
                              <div className="space-y-2">
                                {groupDocs.map((doc) => (
                                  <div 
                                    key={doc.id} 
                                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-primary" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium">{doc.original_filename}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {(doc.file_size / 1024).toFixed(1)} KB
                                          {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                                        </p>
                                      </div>
                                    </div>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleDownloadDoc(doc)}
                                      disabled={downloadingDoc === doc.id}
                                      data-testid={`download-doc-btn-${doc.id}`}
                                      aria-label={`Download ${doc.original_filename}`}
                                    >
                                      {downloadingDoc === doc.id ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Download className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Other documents that don't fit categories */}
                        {prospectDocs.filter(d => 
                          !['insurance_policies', 'loss_runs', 'loss_run', 'claims_history', 'certificate_of_insurance']
                            .some(m => d.category?.includes(m))
                        ).length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                              Other Documents ({prospectDocs.filter(d => 
                                !['insurance_policies', 'loss_runs', 'loss_run', 'claims_history', 'certificate_of_insurance']
                                  .some(m => d.category?.includes(m))
                              ).length})
                            </p>
                            <div className="space-y-2">
                              {prospectDocs.filter(d => 
                                !['insurance_policies', 'loss_runs', 'loss_run', 'claims_history', 'certificate_of_insurance']
                                  .some(m => d.category?.includes(m))
                              ).map((doc) => (
                                <div 
                                  key={doc.id} 
                                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                      <FileText className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium">{doc.original_filename}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {doc.category_display || doc.category} • {(doc.file_size / 1024).toFixed(1)} KB
                                        {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                                      </p>
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleDownloadDoc(doc)}
                                    disabled={downloadingDoc === doc.id}
                                    data-testid={`download-doc-btn-${doc.id}`}
                                    aria-label={`Download ${doc.original_filename}`}
                                  >
                                    {downloadingDoc === doc.id ? (
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Download className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Activity Timeline */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Activity Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {selectedProspect.last_activity && (
                        <div className="flex items-center gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                          <span className="text-muted-foreground">Last activity:</span>
                          <span>{new Date(selectedProspect.last_activity).toLocaleString()}</span>
                        </div>
                      )}
                      {selectedProspect.created_at && (
                        <div className="flex items-center gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-gray-300" />
                          <span className="text-muted-foreground">Created:</span>
                          <span>{new Date(selectedProspect.created_at).toLocaleString()}</span>
                        </div>
                      )}
                      {selectedProspect.broker_name && (
                        <div className="flex items-center gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-gray-300" />
                          <span className="text-muted-foreground">Assigned broker:</span>
                          <span className="font-medium">{selectedProspect.broker_name}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>

      {/* Customer Activation Confirmation Dialog */}
      <Dialog open={showActivateConfirm} onOpenChange={setShowActivateConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-500" />
              Activate Customer
            </DialogTitle>
            <DialogDescription>
              Convert {selectedProspect?.organization_name} from prospect to active customer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-700">
                <p className="font-medium mb-2">This will:</p>
                <ul className="text-sm space-y-1 ml-2">
                  <li>• Change status to "Active Customer"</li>
                  <li>• Unlock all platform modules</li>
                  <li>• Preserve assessment data and documents</li>
                  <li>• Send activation email to organization</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            <div>
              <Label>Activation Notes (Optional)</Label>
              <Textarea
                value={activationNotes}
                onChange={(e) => setActivationNotes(e.target.value)}
                placeholder="Add any notes about this activation..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleActivateCustomer} 
              disabled={activating}
              className="bg-green-600 hover:bg-green-700"
            >
              {activating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Activate Customer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={showStatusUpdate} onOpenChange={setShowStatusUpdate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Prospect Status</DialogTitle>
            <DialogDescription>
              Change the pipeline status for {selectedProspect?.organization_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select 
                value={statusUpdateData.status} 
                onValueChange={(value) => setStatusUpdateData({ ...statusUpdateData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STAGE_CONFIG).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={statusUpdateData.notes}
                onChange={(e) => setStatusUpdateData({ ...statusUpdateData, notes: e.target.value })}
                placeholder="Add notes about this status change..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusUpdate(false)}>Cancel</Button>
            <Button onClick={handleStatusUpdate} disabled={!statusUpdateData.status}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broker Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { if (!open) setShowUploadDialog(false); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5" />
              Upload Document
            </DialogTitle>
            <DialogDescription>
              Upload a document on behalf of {selectedProspect?.name || 'this organization'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Document Category</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="upload-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="certificate_of_insurance">Certificate of Insurance</SelectItem>
                  <SelectItem value="general_liability">General Liability Policy</SelectItem>
                  <SelectItem value="abuse_molestation">Abuse & Molestation Policy</SelectItem>
                  <SelectItem value="directors_officers">Directors & Officers Policy</SelectItem>
                  <SelectItem value="accident_medical">Accident/Medical Policy</SelectItem>
                  <SelectItem value="cyber_liability">Cyber Liability Policy</SelectItem>
                  <SelectItem value="property_coverage">Property Coverage</SelectItem>
                  <SelectItem value="umbrella_excess">Umbrella/Excess Policy</SelectItem>
                  <SelectItem value="auto_insurance">Auto Insurance</SelectItem>
                  <SelectItem value="workers_compensation">Workers Compensation</SelectItem>
                  <SelectItem value="other">Other Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Brief description of the document"
                className="bg-secondary/50"
                data-testid="upload-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Select File</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                onChange={handleBrokerUpload}
                disabled={uploadingDoc}
                className="bg-secondary/50"
                data-testid="upload-file-input"
              />
              <p className="text-xs text-muted-foreground">Supported: PDF, DOC, DOCX, JPG, PNG, XLS, XLSX</p>
            </div>
            {uploadingDoc && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
